import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";

const About = () => {
  const { t, lang } = useLanguage();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="section-padding bg-white relative"
    >
      <div className="container-page">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: sticky intro */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <span className="chip mb-5">{t.about.eyebrow}</span>
            <h2
              data-testid="about-title"
              className="font-display font-black text-4xl sm:text-5xl text-navy leading-[1.05]"
            >
              {t.about.title}
            </h2>
            <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
              {t.about.lead}
            </p>

            {/* Portrait — affiché en intégralité sur tous formats (desktop + mobile) */}
            <figure className="mt-8 overflow-hidden rounded-2xl border border-border bg-[#F1F5F9]">
              <img
                src={SITE.portrait}
                alt="Dr Richard Aswad — chirurgien orthopédique de l'épaule, du coude et de la main à Marseille (Clinique Juge)"
                className="w-full h-auto block"
                loading="lazy"
                width="1200"
                height="1200"
              />
            </figure>

            {/* Titles */}
            <ul className="mt-6 space-y-2.5">
              {t.about.titles.map((title, i) => (
                <li
                  key={i}
                  data-testid={`about-title-${i}`}
                  className="flex gap-3 text-sm text-foreground/80"
                >
                  <i className="fa-solid fa-check mt-1 text-[#FF5A1F]" aria-hidden="true"></i>
                  <span>{title}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: timeline */}
          <div className="lg:col-span-7">
            <div className="relative">
              <div
                className="absolute left-4 md:left-5 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#FF5A1F] via-navy/30 to-transparent"
                aria-hidden="true"
              ></div>

              <ol className="space-y-10">
                {t.about.milestones.map((m, i) => (
                  <li
                    key={i}
                    data-testid={`about-milestone-${i}`}
                    className="relative pl-14 md:pl-16"
                  >
                    <span
                      className="absolute left-0 top-1 flex h-10 w-10 md:h-11 md:w-11 items-center justify-center rounded-full bg-[#0A192F] text-[10px] font-bold text-white shadow-md ring-4 ring-white"
                    >
                      <span className="text-[11px]">{String(i + 1).padStart(2, "0")}</span>
                    </span>
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <span className="font-display text-xs font-bold uppercase tracking-[0.18em] text-[#FF5A1F]">
                        {m.year}
                      </span>
                      <h3 className="font-display text-lg md:text-xl font-bold text-navy">
                        {m.title}
                      </h3>
                    </div>
                    <p className="mt-2 text-sm md:text-base text-foreground/75 leading-relaxed">
                      {m.text}
                    </p>
                  </li>
                ))}
              </ol>
            </div>

            {/* Values */}
            <div className="mt-16">
              <h3 className="font-display text-2xl font-bold text-navy">
                {t.about.valuesTitle}
              </h3>
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                {t.about.values.map((v, i) => {
                  const iconMap = {
                    stethoscope: "fa-stethoscope",
                    microscope: "fa-microscope",
                    "heart-pulse": "fa-heart-pulse",
                  };
                  return (
                    <div
                      key={i}
                      data-testid={`about-value-${i}`}
                      className="rounded-xl border border-border p-5 bg-white card-hover"
                    >
                      <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#FF5A1F]/10 text-[#FF5A1F]">
                        <i className={`fa-solid ${iconMap[v.icon] || "fa-star"}`} aria-hidden="true"></i>
                      </div>
                      <h4 className="mt-3 font-display font-bold text-navy text-base">
                        {v.title}
                      </h4>
                      <p className="mt-1.5 text-sm text-foreground/70 leading-relaxed">
                        {v.text}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Une histoire de transmission — section sobre, sans fioriture */}
        <div className="mt-24 md:mt-32 max-w-5xl mx-auto">
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-navy">
            {lang === "fr" ? "Une histoire de transmission" : "A story of transmission"}
          </h2>
          <div className="mt-10 md:mt-12 grid md:grid-cols-12 gap-10 md:gap-12 items-start">
            <figure className="md:col-span-5">
              <div className="overflow-hidden rounded-xl bg-[#F8FAFC]">
                <img
                  src="https://customer-assets.emergentagent.com/job_orthopedie-pro/artifacts/c201gn97_JPF%20et%20R%20Aswad.png"
                  alt={lang === "fr"
                    ? "Jean-Pierre Franceschi et le Dr Richard Aswad, le jour de la Légion d'honneur"
                    : "Jean-Pierre Franceschi and Dr Richard Aswad, the day of the Legion of Honor"}
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              <figcaption className="mt-3 text-xs italic text-muted-foreground leading-relaxed">
                {lang === "fr"
                  ? "Avec Jean-Pierre Franceschi, le jour de sa Légion d'honneur. « Nous garderons l'image de l'homme incroyable au destin légendaire. »"
                  : "With Jean-Pierre Franceschi, the day of his Legion of Honor. \"We will keep the image of the incredible man with a legendary destiny.\""}
              </figcaption>
            </figure>

            <div className="md:col-span-7 space-y-5 text-foreground/85 leading-relaxed text-[15px] md:text-base">
              {lang === "fr" ? (
                <>
                  <p>Je n'ai pas choisi l'épaule. Elle s'est imposée à moi, au fil de deux rencontres qui ont tout changé.</p>
                  <p>À Paris d'abord, dans le service du Professeur Daniel Goutallier. C'est là que j'ai compris que l'épaule n'était pas une articulation comme les autres — la plus libre du corps humain, la plus complexe à comprendre, la plus exigeante à soigner. Ses travaux sur la coiffe des rotateurs restent une référence mondiale. Il m'a transmis l'exigence intellectuelle.</p>
                  <p>Puis Marseille. Et Jean-Pierre Franceschi.</p>
                  <p>Pendant plusieurs années, j'ai été son interne, son assistant, son compagnon de bloc. Certains gestes chirurgicaux étaient si fluides, si précis qu'ils ressemblaient à de la musique. Jean-Pierre était bassiste. Ce n'était peut-être pas un hasard.</p>
                  <p>Ce qui frappait chez lui n'était pas sa notoriété — pourtant immense, des vestiaires de l'OM aux salles d'opération internationales. C'était sa simplicité. Il avançait sans chercher à paraître, avec cette rare capacité à faire les choses sérieusement sans jamais se prendre au sérieux.</p>
                  <p>Au tournant des années 2000, il m'a proposé de développer à ses côtés la chirurgie de l'épaule. Cette confiance a orienté toute la suite.</p>
                  <p>Jean-Pierre Franceschi nous a quittés le 17 avril 2017. Brutalement, trop tôt. Il avait 64 ans et l'énergie de quelqu'un qui ne compte pas finir.</p>
                  <p>La photo ci-contre a été prise le jour de sa décoration de la Légion d'honneur. On rit tous les deux. C'est lui, exactement.</p>
                  <p>IMOTEpS porte quelque chose de cet héritage : une chirurgie précise, mesurée, au service du mouvement et de la qualité de vie. Pas une démonstration technique. Une réponse juste, adaptée à chaque patient. Et parfois — souvent — la décision de ne pas opérer.</p>
                  <p>C'est ce que Jean-Pierre m'a appris. C'est ce que j'essaie de transmettre à mon tour.</p>
                </>
              ) : (
                <>
                  <p>I did not choose the shoulder. It came to me, through two encounters that changed everything.</p>
                  <p>First in Paris, in the department of Professor Daniel Goutallier. That is where I understood that the shoulder was unlike any other joint — the freest in the human body, the most complex to grasp, the most demanding to treat. His work on the rotator cuff remains a worldwide reference. From him I inherited intellectual rigor.</p>
                  <p>Then Marseille. And Jean-Pierre Franceschi.</p>
                  <p>For several years I was his resident, his assistant, his companion in the operating theatre. Some of his surgical gestures were so fluid, so precise that they sounded like music. Jean-Pierre was a bass player. Perhaps that was not a coincidence.</p>
                  <p>What struck you about him was not his fame — although immense, from the OM locker rooms to international operating rooms. It was his simplicity. He moved forward without seeking to appear, with that rare capacity to take things seriously without ever taking himself seriously.</p>
                  <p>At the turn of the 2000s, he asked me to develop shoulder surgery alongside him. That trust shaped everything that followed.</p>
                  <p>Jean-Pierre Franceschi left us on 17 April 2017. Suddenly, far too soon. He was 64 and had the energy of someone who had no intention of stopping.</p>
                  <p>The photograph shown here was taken on the day of his Legion of Honor decoration. We are both laughing. That is exactly who he was.</p>
                  <p>IMOTEpS carries something of this legacy: precise, measured surgery, in the service of movement and quality of life. Not a technical demonstration. A right answer, fitted to each patient. And sometimes — often — the decision not to operate.</p>
                  <p>That is what Jean-Pierre taught me. That is what I try, in turn, to pass on.</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
